import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import { connect } from 'react-redux';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import { compose } from '@reduxjs/toolkit';
import { useParams } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import LocalPhoneOutlinedIcon from '@mui/icons-material/LocalPhoneOutlined';
import styled from '@mui/material/styles/styled';
import Typography from '@mui/material/Typography';
import MailOutlineOutlinedIcon from '@mui/icons-material/MailOutlineOutlined';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';

import Avatar from '@mui/material/Avatar';
import Skeleton from 'core/skeleton/Skeleton';

import { buildRoute } from 'routes';
import isEmpty from 'helpers/isEmpty';
import useCountries from 'hooks/useCountries';
import ActionButton from 'core/buttons/ActionButton';

const AccountCardContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  display: 'flex',
  gap: theme.spacing(2),
  backgroundColor: 'inherit',
  // border: `1px solid ${theme.palette.grey[200]}`,
  borderRadius: 15,
  boxShadow: '1px 3px 6px 1px rgba(0,0,0,0.3)',
}));

const AccountLogo = styled(Avatar)(({ theme }) => ({
  height: '80px',
  width: '80px',
  background: theme.palette.primary.soft,
  color: theme.palette.primary.main,
  fontWeight: theme.typography.fontWeightBold,
}));

const AccountDetails = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
}));

const AccountActions = styled(Box)(({ theme }) => ({
  display: 'flex',
  marginRight: 'auto',
  flexGrow: 1,
  alignSelf: 'baseline',
  justifyContent: 'end',
}));

function AccountCard({ isLoading, account }) {
  const { getCountryNameByIso3Code } = useCountries();

  const navigate = useNavigate();

  const params = useParams();

  if (isLoading)
    return (
      <AccountCardContainer elevation={0}>
        <Skeleton sx={{ width: '80px', height: '80px', borderRadius: '4px' }} />
        <AccountDetails sx={{ gap: 1 }}>
          <Skeleton sx={{ width: '250px', height: '32px', borderRadius: '4px' }} />
          <Stack
            flexDirection="row"
            alignItems="center"
            gap={1}
            sx={{ color: (theme) => theme.palette.primary.main }}
          >
            <Skeleton sx={{ width: '200px', height: '16px', borderRadius: '4px' }} />
          </Stack>
          <Stack
            flexDirection="row"
            alignItems="center"
            gap={1}
            sx={{ color: (theme) => theme.palette.primary.main }}
          >
            <Skeleton sx={{ width: '180px', height: '12px', borderRadius: '10px' }} />
          </Stack>
        </AccountDetails>
        <AccountActions>
          <Skeleton sx={{ width: '130px', height: '37px', borderRadius: '4px' }} />
        </AccountActions>
      </AccountCardContainer>
    );

  const accountName = account.name;

  const accountShortName = account?.first_name?.charAt(0) + account?.last_name?.charAt(0);

  return (
    <AccountCardContainer elevation={0}>
      <AccountLogo variant="circular">{accountShortName}</AccountLogo>
      <AccountDetails sx={{ gap: 0.8 }}>
        <Typography variant="h5" fontWeight={800} letterSpacing={1.5}>
          {accountName}
        </Typography>
        <Grid gap={0.3} container>
          {[
            {
              icon: <MailOutlineOutlinedIcon sx={{ fontSize: 'inherit' }} />,
              value: account.email,
            },
            {
              icon: <LocalPhoneOutlinedIcon sx={{ fontSize: 'inherit' }} />,
              value: account.phone_number,
            },
            {
              icon: <AdminPanelSettingsOutlinedIcon sx={{ fontSize: 'inherit' }} />,
              value: account.user_type,
            },
          ].map((item, i) => (
            <Grid item xs={12} md={12} key={i}>
              <Stack
                flexDirection="row"
                alignItems="center"
                gap={1}
                sx={{ color: (theme) => theme.palette.grey[600] }}
              >
                {item.icon}
                <Typography>{item.value}</Typography>
              </Stack>
            </Grid>
          ))}
        </Grid>
      </AccountDetails>
      <AccountActions>
        {isLoading ? (
          <Skeleton sx={{ width: '130px', height: '37px', borderRadius: '4px' }} />
        ) : (
          <ActionButton onClick={() => navigate(buildRoute('accounts.edit', params.accountId))}>
            Edit Account
          </ActionButton>
        )}
      </AccountActions>
    </AccountCardContainer>
  );
}

export default compose(connect(mapStateToProps))(AccountCard);

AccountCard.propTypes = {
  account: PropTypes.object,
  isLoading: PropTypes.bool,
};

AccountCard.defaultProps = {
  account: {},
  isLoading: false,
};

function mapStateToProps(state, ownProps) {
  return {
    ...ownProps,
    ...(!Object.prototype.hasOwnProperty.call(ownProps, 'account')
      ? {
          account: state.accounts.show.data || {},
        }
      : {}),
    ...(!Object.prototype.hasOwnProperty.call(ownProps, 'isLoading')
      ? {
          isLoading: state.accounts.show.isLoading || isEmpty(state.accounts.show.data),
        }
      : {}),
  };
}
